import _ from "lodash";
import React, { useState, useMemo } from "react";
import useVirtualizedUserDetails from "MyCalendar/hooks/useVirtualizedUserDetails";
import PropTypes from "prop-types";

import update from "immutability-helper";
import classNames from "classnames";

import { ADD_MEMBERS_COHORTS } from "MessagingV2/modules/Utils";
import ViewMembers from "./ViewMembers";
import UsersAndCohorts from "./UsersAndCohorts";
import MetadataProvider from "./MetadataProvider";

import classes from "./DualPanelManageMembers.scss";

const USERS = "USERS";
const initialState = {
  [ADD_MEMBERS_COHORTS.YEAR_GROUP]: {},
  [ADD_MEMBERS_COHORTS.GRADE]: {},
  [ADD_MEMBERS_COHORTS.CLASSES]: {},
  //   sample structure with data
  //   [cohort_type]: { [id]: { usersIds: Set, removedUsersIds: Set}}
  // if contain remove use id then - intermediate state  this will sync when use user added or removed from

  [USERS]: {}, // this will contain all the users that are selected [id]: User as object
};

const DualPanelManageMembers = ({
  handleAddUsers,
  handleRemoveUsers,
  prevUsers = [],
  metadataProps,
  renderQueryWrapper,
  initUsersState = {},
  overrideClasses = {},
  dsVersion = "1.0",
  showTypeFilter = false,
  searchPlaceholder,
  testId,
  showSearchAndFilter,
  typeFilterOptions = [],
  showEmptyTextQuery = false,
  emptyTextQueryCond = () => {},
  showClearAllBtn = false,
  autoFocusSearchInput = true,
  isChecklistFilter = true,
  isRevampEnabled = false,
  showStudentId = false,
  sharedWith = [],
  showEmptyStateForMembersList = false,
  getDisabledFilterOptions = () => [],
  leftCustomHeader = null,
  enableLazyHydration = false,
  showSchoolName = false,
  staffCurriculumProgramLabel,
}) => {
  const [cohortAndUserState, setCohortAndUserState] = useState({
    ...initialState,
    [USERS]: initUsersState,
  });
  const [isResultEmpty, setIsResultEmpty] = useState(false);
  const componentTestId = testId
    ? `${testId}-dualPaneManageMembers`
    : undefined;

  const handleClearAll = () => {
    handleRemoveUsers(_.values(cohortAndUserState.USERS));
    setCohortAndUserState(initialState);
  };

  const addSelectedUsers = (users, cb) => {
    const userIds = new Set(users.map(user => user.id));
    setCohortAndUserState(prevState => {
      const updatedState = _.isFunction(cb) ? cb(prevState) : prevState;
      const stateWithAddedUsers = update(updatedState, {
        [USERS]: {
          $set: {
            ...updatedState[USERS],
            ...users.reduce((acc, user) => {
              acc[user.id] = user;
              return acc;
            }, {}),
          },
        },
      });

      const stateWithSyncCohortsState = update(stateWithAddedUsers, {
        $apply: state =>
          _.mapValues(state, (cohortTypeState, cohortType) => {
            if (!_.includes(_.values(ADD_MEMBERS_COHORTS), cohortType))
              return cohortTypeState;

            return _.mapValues(cohortTypeState, cohortData =>
              update(cohortData, {
                removedUsersIds: {
                  $apply: removedUsersIds =>
                    new Set(
                      Array.from(removedUsersIds).filter(
                        removedUserId => !userIds.has(removedUserId)
                      )
                    ),
                },
              })
            );
          }),
      });

      return stateWithSyncCohortsState;
    });
    handleAddUsers(users);
  };

  const removeSelectedUsers = (users, cb) => {
    const userIdsList = _.map(users, "id");
    const userIds = new Set(userIdsList); // Using Set for faster lookup
    setCohortAndUserState(prevState => {
      const updatedState = _.isFunction(cb) ? cb(prevState) : prevState;
      const stateWithRemovedUser = update(updatedState, {
        [USERS]: {
          $unset: userIdsList,
        },
      });
      const stateWithSyncCohortsState = update(stateWithRemovedUser, {
        $apply: state => {
          const newState = _.mapValues(state, (cohortTypeState, cohortType) => {
            if (!_.includes(_.values(ADD_MEMBERS_COHORTS), cohortType))
              return cohortTypeState;

            return _.mapValues(cohortTypeState, cohortData => {
              if (!cohortData) return undefined;

              const commonUserIds = new Set(
                [...cohortData.usersIds].filter(userId => userIds.has(userId))
              );

              // Add commonUserIds to removedUsersIds set
              const updatedRemovedUsersIds = new Set([
                ...cohortData.removedUsersIds,
                ...commonUserIds,
              ]);

              // Delete the cohort if removedUsersIds and usersIds are same
              if (
                _.isEqual(
                  _.size(updatedRemovedUsersIds),
                  _.size(cohortData.usersIds)
                )
              ) {
                return undefined; // Returning undefined deletes the key
              }

              return {
                ...cohortData,
                removedUsersIds: updatedRemovedUsersIds,
              };
            });
          });

          // Remove empty cohorts
          Object.keys(newState).forEach(cohortType => {
            if (!_.includes(ADD_MEMBERS_COHORTS, cohortType)) return newState;
            newState[cohortType] = _.pickBy(
              newState[cohortType],
              cohortData => cohortData !== undefined
            );
          });

          return newState;
        },
      });
      return stateWithSyncCohortsState;
    });

    handleRemoveUsers(users);
  };

  const onAddCohort = ({ users, cohortType, cohortId }) => {
    const stateAfterCohortAdded = prevState => {
      const usersIds = new Set(users.map(user => user.id));
      return update(prevState, {
        [cohortType]: {
          [cohortId]: {
            $set: {
              usersIds,
              removedUsersIds: new Set(),
            },
          },
        },
      });
    };
    addSelectedUsers(users, stateAfterCohortAdded);
  };

  const onRemoveCohort = ({ cohortType, cohortId }) => {
    const userIds = _.toArray(
      _.get(cohortAndUserState, [cohortType, cohortId, "usersIds"], [])
    );

    const users = _.map(userIds, userId =>
      _.get(cohortAndUserState, [USERS, userId], {})
    );

    const stateAfterCohortRemove = prevState => {
      return update(prevState, {
        [cohortType]: {
          $unset: [cohortId],
        },
      });
    };

    if (!_.isEmpty(users)) removeSelectedUsers(users, stateAfterCohortRemove);
  };

  const selectedUsers = useMemo(() => {
    return _.values(cohortAndUserState.USERS);
  }, [cohortAndUserState.USERS]);

  const { getUserDetails, onRangeChanged } = useVirtualizedUserDetails({
    users: selectedUsers,
    enabled: enableLazyHydration,
  });

  return (
    <MetadataProvider value={metadataProps}>
      <div className={classes.rootContainer}>
        <div className={classes.cohortsAndUsersContainer}>
          <UsersAndCohorts
            cohortState={cohortAndUserState}
            selectedUsersState={cohortAndUserState.USERS}
            onAddUsers={addSelectedUsers}
            onRemoveUsers={removeSelectedUsers}
            onAddCohort={onAddCohort}
            onRemoveCohort={onRemoveCohort}
            prevUsers={prevUsers}
            showTypeFilter={showTypeFilter}
            typeFilterOptions={typeFilterOptions}
            dsVersion={dsVersion}
            searchPlaceholder={searchPlaceholder}
            renderQueryWrapper={renderQueryWrapper}
            testId={componentTestId}
            showSearchAndFilter={showSearchAndFilter}
            showEmptyTextQuery={showEmptyTextQuery}
            emptyTextQueryCond={emptyTextQueryCond}
            autoFocusSearchInput={autoFocusSearchInput}
            isChecklistFilter={isChecklistFilter}
            isRevampEnabled={isRevampEnabled}
            showStudentId={showStudentId}
            setIsResultEmpty={setIsResultEmpty}
            getDisabledFilterOptions={getDisabledFilterOptions}
            leftCustomHeader={leftCustomHeader}
            enableLazyHydration={enableLazyHydration}
            showSchoolName={showSchoolName}
            staffCurriculumProgramLabel={staffCurriculumProgramLabel}
          />
        </div>
        <div
          className={classNames(
            classes.usersViewContainer,
            overrideClasses.usersViewContainer,
            {
              [classes.emptyState]: _.isEmpty(cohortAndUserState.USERS),
            }
          )}
        >
          <ViewMembers
            users={selectedUsers}
            onRemoveUsers={removeSelectedUsers}
            handleClearAll={handleClearAll}
            showClearAllBtn={showClearAllBtn}
            isRevampEnabled={isRevampEnabled}
            sharedWith={sharedWith}
            showStudentId={showStudentId}
            showEmptyStateForMembersList={
              showEmptyStateForMembersList && !isResultEmpty
            }
            testId={componentTestId}
            onRangeChanged={undefined}
            getUserDetails={enableLazyHydration ? getUserDetails : undefined}
          />
        </div>
      </div>
    </MetadataProvider>
  );
};

DualPanelManageMembers.propTypes = {
  metadataProps: PropTypes.shape({
    emptyStateSubtitle: PropTypes.string.isRequired,
    countUsersSelectedText: PropTypes.string.isRequired,
    selectedUsersListText: PropTypes.string.isRequired,
  }).isRequired,
  renderQueryWrapper: PropTypes.func.isRequired,
  handleAddUsers: PropTypes.func.isRequired,
  handleRemoveUsers: PropTypes.func.isRequired,
  prevUsers: PropTypes.array,
};

export default DualPanelManageMembers;
